import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  HardHat, ArrowLeft, BookOpen,
  Home, Wrench, Thermometer, Droplet, Zap, Paintbrush,
  TreePine, Layers, Sun,
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  roofing: Home, remodeling: Wrench, hvac: Thermometer,
  plumbing: Droplet, electrical: Zap, painting: Paintbrush,
  flooring: Layers, landscaping: TreePine, windows: Layers,
  concrete: Layers, solar: Sun,
};

const TipDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const language = i18n.language?.startsWith('es') ? 'es' : 'en';

  const [tip, setTip] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    axios
      .get(`${API}/tips/by-slug/${slug}?language=${language}`)
      .then((r) => {
        setTip(r.data.tip);
        setRelated(r.data.related || []);
      })
      .catch((e) => {
        if (e.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, language]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">
        {t('common.loading')}
      </div>
    );
  }

  if (notFound || !tip) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4 text-center">
        <SEO title="Article not found" description="The article you're looking for doesn't exist." path={`/tips/${slug}`} />
        <h1 className="text-3xl font-black text-slate-900 mb-3">Article not found</h1>
        <p className="text-slate-600 mb-6">This article may have moved or been removed.</p>
        <button onClick={() => navigate('/tips')} className="bg-orange-600 text-white font-bold px-6 py-3 rounded-sm">
          Browse all tips
        </button>
      </div>
    );
  }

  const CategoryIcon = CATEGORY_ICONS[tip.category] || Wrench;
  const plain = (s) => (s || '').replace(/\s+/g, ' ').trim();
  // Prefer the LLM-crafted meta_description; fall back to first 160 chars of introduction.
  const description = (tip.meta_description && tip.meta_description.length <= 165)
    ? tip.meta_description
    : plain(tip.introduction).slice(0, 160);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={`${tip.title} | ContractorsLab Tips`}
        description={description}
        keywords={`${tip.category}, home improvement, ${tip.topic || ''}, hire contractor, ${tip.title}`}
        path={`/tips/${tip.slug}`}
      />

      {/* Nav */}
      <nav className="bg-slate-900 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="tip-home-link">
              <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-black text-white tracking-tight hidden sm:block">CONTRACTORSLAB</span>
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="dark" />
              <Link to="/tips" className="text-slate-300 hover:text-white font-medium text-sm flex items-center gap-1" data-testid="tip-back-to-tips">
                <ArrowLeft className="w-4 h-4" />
                {t('tips.backToTips')}
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="max-w-4xl mx-auto px-4 md:px-8 pt-5 text-xs text-slate-500">
        <ol className="flex items-center gap-2">
          <li><Link to="/" className="hover:text-orange-600">Home</Link></li>
          <li aria-hidden>/</li>
          <li><Link to="/tips" className="hover:text-orange-600">Tips &amp; Guides</Link></li>
          <li aria-hidden>/</li>
          <li className="text-slate-700 truncate max-w-[300px]" aria-current="page">{tip.title}</li>
        </ol>
      </nav>

      {/* Article */}
      <article className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center gap-2 mb-4 text-xs">
          <span className="flex items-center gap-1.5 bg-orange-600 text-white px-2.5 py-1 rounded-sm font-bold uppercase tracking-wide">
            <CategoryIcon className="w-3.5 h-3.5" />
            {tip.category}
          </span>
        </div>

        <h1
          className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 tracking-tight leading-tight mb-5"
          data-testid="tip-detail-title"
        >
          {tip.title}
        </h1>

        {tip.introduction && (
          <p className="text-lg text-slate-700 leading-relaxed mb-8">{tip.introduction}</p>
        )}

        {tip.key_points?.length > 0 && (
          <section className="mb-8">
            <h2 className="text-xl font-black text-slate-900 mb-4 uppercase tracking-tight">
              {t('tips.keyPoints')}
            </h2>
            <div className="space-y-4">
              {tip.key_points.map((pt, idx) => (
                <div key={idx} className="bg-white border-l-4 border-orange-600 pl-4 pr-4 py-3 rounded-r-sm">
                  <h3 className="font-bold text-slate-900 mb-1">{pt.heading}</h3>
                  <p className="text-slate-700 text-sm leading-relaxed">{pt.content}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {tip.when_to_call_pro && (
          <section className="bg-orange-50 border-l-4 border-orange-600 p-5 mb-6 rounded-r-sm">
            <h2 className="font-black text-slate-900 mb-2 uppercase text-sm tracking-wide">
              {t('tips.whenToCallPro')}
            </h2>
            <p className="text-slate-700 text-sm leading-relaxed">{tip.when_to_call_pro}</p>
          </section>
        )}

        {tip.cost_estimate && (
          <section className="bg-slate-100 p-5 mb-8 rounded-sm">
            <h2 className="font-black text-slate-900 mb-2 uppercase text-sm tracking-wide">
              {t('tips.costEstimate')}
            </h2>
            <p className="text-slate-700 font-medium">{tip.cost_estimate}</p>
          </section>
        )}

        {/* CTA */}
        <div className="border-t border-slate-200 pt-6 flex justify-end">
          <Link
            to="/register?role=client"
            className="flex items-center gap-2 text-sm font-bold text-orange-600 hover:text-orange-700"
            data-testid="tip-post-job-cta"
          >
            Need a pro for this? Post your job free →
          </Link>
        </div>
      </article>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 md:px-8 py-10 border-t border-slate-200">
          <h2 className="text-xl font-black text-slate-900 mb-5 uppercase tracking-tight flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-orange-600" />
            {t('tips.relatedArticles') || 'Related articles'}
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {related.map((r) => {
              const RIcon = CATEGORY_ICONS[r.category] || Wrench;
              return (
                <Link
                  key={r.id}
                  to={`/tips/${r.slug}`}
                  className="group bg-white border border-slate-200 hover:border-orange-600 hover:shadow-lg transition-all p-5 rounded-sm block"
                  data-testid={`related-tip-${r.slug}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-orange-100 group-hover:bg-orange-600 rounded-sm flex items-center justify-center transition-colors">
                      <RIcon className="w-4 h-4 text-orange-600 group-hover:text-white transition-colors" />
                    </div>
                    <span className="text-xs uppercase font-bold text-slate-500">{r.category}</span>
                  </div>
                  <h3 className="font-black text-slate-900 leading-tight mb-1 group-hover:text-orange-600">
                    {r.title}
                  </h3>
                  <p className="text-sm text-slate-600 line-clamp-2">{r.introduction}</p>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* JSON-LD Article structured data for richer SERP snippets */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: tip.title,
            description,
            articleSection: tip.category,
            inLanguage: tip.language,
            datePublished: tip.created_at,
            author: { '@type': 'Organization', name: 'ContractorsLab' },
            publisher: {
              '@type': 'Organization',
              name: 'ContractorsLab',
              logo: { '@type': 'ImageObject', url: 'https://contractorslab.com/social-logo-facebook-square.png' },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://contractorslab.com/tips/${tip.slug}`,
            },
          }),
        }}
      />
    </div>
  );
};

export default TipDetailPage;
